// Multi-select with a cap, using the page's `.checks` / `.check-opt` markup.
// Cap enforcement: if adding would exceed `max`, the new option is silently
// not added (mirrors the vanilla draft's "e.target.checked = false" behavior).

export interface CheckOption<V extends string> {
  value: V;
  label: React.ReactNode;
}

interface Props<V extends string> {
  name: string;
  group?: string;
  values: ReadonlyArray<V>;
  onChange: (next: V[]) => void;
  options: ReadonlyArray<CheckOption<V>>;
  max?: number;
}

export function CheckGroup<V extends string>({
  name,
  group,
  values,
  onChange,
  options,
  max,
}: Props<V>): JSX.Element {
  const set = new Set(values);

  const toggle = (v: V): void => {
    if (set.has(v)) {
      onChange(values.filter((x) => x !== v));
    } else {
      if (max !== undefined && values.length >= max) return;
      onChange([...values, v]);
    }
  };

  return (
    <div className="checks" data-group={group} data-max={max}>
      {options.map((opt) => {
        const selected = set.has(opt.value);
        return (
          <label
            key={opt.value}
            className={"check-opt" + (selected ? " selected" : "")}
          >
            <input
              type="checkbox"
              name={name}
              value={opt.value}
              checked={selected}
              onChange={() => toggle(opt.value)}
            />
            <span>{opt.label}</span>
          </label>
        );
      })}
    </div>
  );
}
